/**
 * Onboarding Guide Component
 * Shows step-by-step guidance for first-time users
 */
import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Lightbulb } from 'lucide-react';

const STORAGE_KEY = 'yobitch_onboarding_completed';

/**
 * Get onboarding completion status
 */
export function isOnboardingComplete(featureId) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return data[featureId] === true;
  } catch {
    return false;
  }
}

/**
 * Mark onboarding as complete
 */
export function completeOnboarding(featureId) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    data[featureId] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silent fail
  }
}

/**
 * Reset onboarding (for testing)
 */
export function resetOnboarding(featureId) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    delete data[featureId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silent fail
  }
}

/**
 * OnboardingGuide - Step-by-step guide overlay
 */
export const OnboardingGuide = ({ featureId, steps = [], onComplete, forceShow = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  // Initialize visible state directly from localStorage check
  const [visible, setVisible] = useState(() => {
    return forceShow || !isOnboardingComplete(featureId);
  });

  if (!visible || steps.length === 0) return null;

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;

  const handleNext = () => {
    if (isLast) {
      completeOnboarding(featureId);
      setVisible(false);
      onComplete?.();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding(featureId);
    setVisible(false);
    onComplete?.();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '400px',
          background:
            'linear-gradient(180deg, rgba(30, 30, 40, 0.98) 0%, rgba(20, 20, 30, 0.98) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(127, 255, 0, 0.2)',
          overflow: 'hidden',
          animation: 'slideUp 0.3s ease',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(127, 255, 0, 0.1)',
            borderBottom: '1px solid rgba(127, 255, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Lightbulb size={18} style={{ color: 'var(--accent-green)' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>Quick Guide</span>
          </div>
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
            }}
          >
            <X size={18} style={{ color: 'rgba(255,255,255,0.5)' }} />
          </button>
        </div>

        {/* Step Content */}
        <div style={{ padding: '24px 20px' }}>
          {/* Step indicator */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              marginBottom: '16px',
            }}
          >
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '3px',
                  borderRadius: '2px',
                  background: i <= currentStep ? 'var(--accent-green)' : 'rgba(255,255,255,0.1)',
                  transition: 'background 0.3s ease',
                }}
              />
            ))}
          </div>

          {/* Icon */}
          {step.icon && (
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: `${step.color || 'var(--accent-green)'}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              {React.createElement(step.icon, {
                size: 24,
                style: { color: step.color || 'var(--accent-green)' },
              })}
            </div>
          )}

          {/* Title */}
          <h3
            style={{
              margin: '0 0 8px',
              fontSize: '16px',
              fontWeight: 600,
              color: '#fff',
            }}
          >
            {step.title}
          </h3>

          {/* Description */}
          <p
            style={{
              margin: '0 0 20px',
              fontSize: '13px',
              lineHeight: '1.5',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            {step.description}
          </p>

          {/* Tip */}
          {step.tip && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(245, 158, 11, 0.2)',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 600,
                  color: '#F59E0B',
                  marginBottom: '4px',
                }}
              >
                PRO TIP
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{step.tip}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            background: 'rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button
            onClick={handlePrev}
            disabled={isFirst}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              color: isFirst ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.7)',
              fontSize: '12px',
              cursor: isFirst ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <ChevronLeft size={14} />
            Back
          </button>

          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
            {currentStep + 1} of {steps.length}
          </span>

          <button
            onClick={handleNext}
            style={{
              padding: '8px 16px',
              background: 'var(--accent-green)',
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            {isLast ? 'Got it!' : 'Next'}
            {!isLast && <ChevronRight size={14} />}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

/**
 * Stealth Launch Guide Steps
 */
export const STEALTH_LAUNCH_GUIDE_STEPS = [
  {
    title: 'Welcome to Stealth Launch',
    description:
      'Max Tier gives you powerful automation tools to launch tokens professionally. This quick guide will show you the key features.',
    tip: 'Take 2 minutes to read this guide - it will save you hours of trial and error.',
  },
  {
    title: 'Generate Stealth Wallets',
    description:
      'Create 10-20 fresh wallets that will buy your token at launch. More wallets = harder to detect as a coordinated buy.',
    tip: 'Fund from different sources (CEX, external wallets) for maximum stealth. Never fund all wallets from the same wallet.',
  },
  {
    title: 'Volume Bot',
    description:
      'After launch, the Volume Bot automatically generates organic-looking trading activity using your bundle wallets.',
    tip: 'Start with Medium intensity. High intensity may trigger detection on some platforms.',
  },
  {
    title: 'Auto-Sell Triggers',
    description:
      'Set automatic profit-taking at specific price multipliers. Example: Sell 30% at 2x, 40% at 5x, 20% at 10x.',
    tip: 'Always set a stop loss (-30% to -50% recommended) to protect against complete loss.',
  },
  {
    title: 'Quick Fund Wallets',
    description:
      'Use the QR codes or copy addresses to fund each wallet. The system tracks funding status in real-time.',
    tip: 'You need at least 50% of wallets funded to launch. All wallets funded is recommended.',
  },
];

export default OnboardingGuide;
